/**
 * TanStack Query hooks for Anchor Browser API
 * Provides reactive data fetching with caching and optimistic updates
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query'
import { anchorBrowserApi, AnchorBrowserApiError } from '../lib/anchor-browser-api'
import type {
  Task,
  CreateTaskRequest,
  CreateTaskResponse,
  ListTasksParams,
  ListTasksResponse,
  ScheduledTask,
  CreateScheduledTaskRequest,
  UpdateScheduledTaskRequest,
  ListScheduledTasksParams,
  ListScheduledTasksResponse,
  TaskMedia
} from '../types/anchor-browser'

// Query keys
export const anchorBrowserKeys = {
  all: ['anchor-browser'] as const,
  tasks: () => [...anchorBrowserKeys.all, 'tasks'] as const,
  task: (id: string) => [...anchorBrowserKeys.tasks(), id] as const,
  taskMedia: (id: string) => [...anchorBrowserKeys.task(id), 'media'] as const,
  scheduledTasks: () => [...anchorBrowserKeys.all, 'scheduled-tasks'] as const,
  scheduledTask: (id: string) => [...anchorBrowserKeys.scheduledTasks(), id] as const,
}

// Task hooks
export function useTasks(
  params: ListTasksParams = {},
  options?: UseQueryOptions<ListTasksResponse, AnchorBrowserApiError>
) {
  return useQuery({
    queryKey: [...anchorBrowserKeys.tasks(), params],
    queryFn: () => anchorBrowserApi.listTasks(params),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute for real-time updates
    ...options,
  })
}

export function useTask(
  taskId: string,
  options?: UseQueryOptions<Task, AnchorBrowserApiError>
) {
  return useQuery({
    queryKey: anchorBrowserKeys.task(taskId),
    queryFn: () => anchorBrowserApi.getTask(taskId),
    enabled: !!taskId,
    staleTime: 5000, // 5 seconds for faster updates
    refetchInterval: (query) => {
      const status = query.state.data?.status
      // Refetch more frequently for active tasks
      if (status === 'queued') return 3000 // 3 seconds - waiting to start
      if (status === 'running') return 2000 // 2 seconds - actively executing
      if (status === 'created') return 5000 // 5 seconds - legacy status
      // Don't refetch for completed/failed tasks
      if (status === 'completed' || status === 'finished' || status === 'failed') return false
      return 10000 // Default polling for unknown statuses
    },
    ...options,
  })
}

export function useTaskMedia(
  taskId: string,
  options?: UseQueryOptions<TaskMedia, AnchorBrowserApiError>
) {
  return useQuery({
    queryKey: anchorBrowserKeys.taskMedia(taskId),
    queryFn: () => anchorBrowserApi.getTaskMedia(taskId),
    enabled: !!taskId,
    staleTime: 60000, // 1 minute
    ...options,
  })
}

// Task mutations
export function useCreateTask(
  options?: UseMutationOptions<CreateTaskResponse, AnchorBrowserApiError, CreateTaskRequest>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: anchorBrowserApi.createTask,
    onSuccess: () => {
      // Invalidate tasks list to show new task
      queryClient.invalidateQueries({ queryKey: anchorBrowserKeys.tasks() })
    },
    ...options,
  })
}

export function useStopTask(
  options?: UseMutationOptions<void, AnchorBrowserApiError, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: anchorBrowserApi.stopTask,
    onSuccess: (_, taskId) => {
      // Invalidate specific task and tasks list
      queryClient.invalidateQueries({ queryKey: anchorBrowserKeys.task(taskId) })
      queryClient.invalidateQueries({ queryKey: anchorBrowserKeys.tasks() })
    },
    ...options,
  })
}

export function usePauseTask(
  options?: UseMutationOptions<void, AnchorBrowserApiError, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: anchorBrowserApi.pauseTask,
    onSuccess: (_, taskId) => {
      // Invalidate specific task and tasks list
      queryClient.invalidateQueries({ queryKey: anchorBrowserKeys.task(taskId) })
      queryClient.invalidateQueries({ queryKey: anchorBrowserKeys.tasks() })
    },
    ...options,
  })
}

export function useResumeTask(
  options?: UseMutationOptions<void, AnchorBrowserApiError, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: anchorBrowserApi.resumeTask,
    onSuccess: (_, taskId) => {
      // Invalidate specific task and tasks list
      queryClient.invalidateQueries({ queryKey: anchorBrowserKeys.task(taskId) })
      queryClient.invalidateQueries({ queryKey: anchorBrowserKeys.tasks() })
    },
    ...options,
  })
}

// Scheduled task hooks
export function useScheduledTasks(
  params: ListScheduledTasksParams = {},
  options?: UseQueryOptions<ListScheduledTasksResponse, AnchorBrowserApiError>
) {
  return useQuery({
    queryKey: [...anchorBrowserKeys.scheduledTasks(), params],
    queryFn: () => anchorBrowserApi.listScheduledTasks(params),
    staleTime: 60000, // 1 minute
    ...options,
  })
}

export function useScheduledTask(
  taskId: string,
  options?: UseQueryOptions<ScheduledTask, AnchorBrowserApiError>
) {
  return useQuery({
    queryKey: anchorBrowserKeys.scheduledTask(taskId),
    queryFn: () => anchorBrowserApi.getScheduledTask(taskId),
    enabled: !!taskId,
    staleTime: 60000, // 1 minute
    ...options,
  })
}

export function useCreateScheduledTask(
  options?: UseMutationOptions<ScheduledTask, AnchorBrowserApiError, CreateScheduledTaskRequest>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: anchorBrowserApi.createScheduledTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: anchorBrowserKeys.scheduledTasks() })
    },
    ...options,
  })
}

export function useUpdateScheduledTask(
  options?: UseMutationOptions<ScheduledTask, AnchorBrowserApiError, UpdateScheduledTaskRequest>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: anchorBrowserApi.updateScheduledTask,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: anchorBrowserKeys.scheduledTask(variables.id) })
      queryClient.invalidateQueries({ queryKey: anchorBrowserKeys.scheduledTasks() })
    },
    ...options,
  })
}

export function useDeleteScheduledTask(
  options?: UseMutationOptions<void, AnchorBrowserApiError, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: anchorBrowserApi.deleteScheduledTask,
    onSuccess: (_, taskId) => {
      queryClient.removeQueries({ queryKey: anchorBrowserKeys.scheduledTask(taskId) })
      queryClient.invalidateQueries({ queryKey: anchorBrowserKeys.scheduledTasks() })
    },
    ...options,
  })
}
